import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { Rating } from '../../rating/rating';
import { TranslateModule } from '@ngx-translate/core';
import { IInfoRating, IQualificationSummary } from '@core/models';
import { City, Geomarker } from '@core/services';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface IQualification {
  qualification: number;
  percentage: number;
  numQualifications: number;
}

/**
 * Valoration card that displays the rating summary and allows requesting an edit.
 */
@Component({
  selector: 'app-valoration-card',
  imports: [Rating, TranslateModule],
  templateUrl: './valoration-card.html',
  styleUrl: './valoration-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValorationCard {
  private readonly geomarkerService = inject(Geomarker);
  private readonly cityService = inject(City);

  /** Variable that receives the city identifier from a parent component */
  readonly city = model<number | null>(null);
  /** Variable that receives the geomarker identifier from a parent component */
  readonly marker = model<number | null>(null);

  /** Indicates whether the item has already been rated */
  readonly hasQualification = input<boolean>(false);

  /** Stores the current user rating or null when absent */
  readonly userQualification = model<number | null>(null);

  /** Rating value as a formatted string */
  readonly qualification = computed<string>(() => {
    const infoCalification = this.infoCalification();
    if (!infoCalification) return '0.0';
    return infoCalification.average.toFixed(1);
  });

  /** Number of valuations or ratings */
  readonly valuations = input<number>(0);

  /** Event emitted when an edit of the rating is requested */
  readonly editCalificationChange = output<void>();

  readonly infoCalification = signal<IInfoRating | null>(null);

  /** Initializes the component and sets up effects and subscriptions. */
  constructor() {
    effect(() => this.getInfoCalification());

    this.geomarkerService
      .getUpdateRatings$()
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.getInfoCalification());
  }

  /** Fetches rating information for the current city or marker and updates the internal state. */
  private async getInfoCalification(): Promise<void> {
    try {
      const markerId = this.marker();
      const cityId = this.city();

      if (!markerId && !cityId) {
        this.infoCalification.set(null);
        return;
      }

      const response = await firstValueFrom(
        markerId
          ? this.geomarkerService.getInfoRatings(markerId)
          : this.cityService.getInfoRatings(cityId!),
      );

      this.infoCalification.set(response);
    } catch (error) {
      console.error('Error fetching rating info:', error);
      this.infoCalification.set(null);
    }
  }

  /** Array of qualifications with their percentage and counts for display */
  protected readonly qualificationArray = computed<IQualification[]>(() => {
    const infoCalification = this.infoCalification();
    if (!infoCalification) return [];
    const qualificationSummary = infoCalification.breakdown_ratings;
    const map = new Map<number, number>();
    if (qualificationSummary) {
      qualificationSummary.forEach((item) => map.set(item.score, item.quantity));
    }

    const totalQualifications = infoCalification.total_ratings || 0;
    const qualificationArray: IQualification[] = [];
    for (let i = 5; i >= 1; i--) {
      const numQualifications = map.get(i) || 0;
      const percentage =
        totalQualifications > 0 ? (numQualifications / totalQualifications) * 100 : 0;
      qualificationArray.push({
        qualification: i,
        percentage,
        numQualifications,
      });
    }

    return qualificationArray;
  });

  /**
   * Sets the internal user qualification model.
   * @param qualification - Numeric value of the qualification to set
   */
  protected setQualification(qualification: number): void {
    this.userQualification.set(qualification);
  }

  /**
   * Requests the start of the qualification edit process by emitting the event.
   */
  protected editCalification(): void {
    this.editCalificationChange.emit();
  }
}
